import React from 'react';

interface BudgetIndicatorProps {
  budget: number;
  spent: number;
  available: number;
  budgetStatus: 'under' | 'over';
  size?: 'sm' | 'md' | 'lg';
  showDetails?: boolean;
}

const BudgetIndicator: React.FC<BudgetIndicatorProps> = ({
  budget,
  spent,
  available,
  budgetStatus,
  size = 'md',
  showDetails = true,
}) => {
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const percentageUsed = (spent / budget) * 100;
  const isOverBudget = budgetStatus === 'over';

  // Size configurations
  const sizeClasses = {
    sm: {
      container: 'p-3',
      title: 'text-xs',
      amount: 'text-lg',
      bar: 'h-2',
      icon: 'w-8 h-8',
      iconSvg: 'w-4 h-4',
    },
    md: {
      container: 'p-4',
      title: 'text-sm',
      amount: 'text-2xl',
      bar: 'h-2.5',
      icon: 'w-10 h-10',
      iconSvg: 'w-5 h-5',
    },
    lg: {
      container: 'p-6',
      title: 'text-base',
      amount: 'text-3xl',
      bar: 'h-3',
      icon: 'w-12 h-12',
      iconSvg: 'w-6 h-6',
    },
  };

  const classes = sizeClasses[size];

  return (
    <div className="space-y-4">
      {/* Progress Bar */}
      <div>
        <div className="flex justify-between text-sm text-gray-600 mb-2">
          <span className="font-medium">Uso del presupuesto</span>
          <span className="font-semibold">
            {Math.min(percentageUsed, 100).toFixed(1)}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`${classes.bar} rounded-full transition-all duration-500 ${
              isOverBudget
                ? 'bg-red-600'
                : percentageUsed > 80
                ? 'bg-yellow-500'
                : 'bg-green-600'
            }`}
            style={{ width: `${Math.min(percentageUsed, 100)}%` }}
          />
        </div>
        {isOverBudget && (
          <div className="mt-1 text-xs text-red-600 font-medium">
            Excedido en {((percentageUsed - 100)).toFixed(1)}%
          </div>
        )}
      </div>

      {/* Budget Details */}
      {showDetails && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {/* Budget */}
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-600 mb-1">Presupuesto</p>
            <p className="text-lg font-bold text-gray-900">
              {formatCurrency(budget)}
            </p>
          </div>

          {/* Spent */}
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-600 mb-1">Gastado</p>
            <p className="text-lg font-bold text-gray-900">
              {formatCurrency(spent)}
            </p>
          </div>

          {/* Available/Over */}
          <div
            className={`rounded-lg p-3 ${
              isOverBudget ? 'bg-red-50' : 'bg-green-50'
            }`}
          >
            <p
              className={`text-xs mb-1 font-medium ${
                isOverBudget ? 'text-red-800' : 'text-green-800'
              }`}
            >
              {isOverBudget ? 'Sobre presupuesto' : 'Disponible'}
            </p>
            <p
              className={`text-lg font-bold ${
                isOverBudget ? 'text-red-600' : 'text-green-600'
              }`}
            >
              {formatCurrency(Math.abs(available))}
            </p>
          </div>
        </div>
      )}

      {/* Visual Status Card */}
      <div
        className={`${classes.container} rounded-lg ${
          isOverBudget ? 'bg-red-50 border-2 border-red-200' : 'bg-green-50 border-2 border-green-200'
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p
              className={`${classes.title} font-medium mb-1 ${
                isOverBudget ? 'text-red-800' : 'text-green-800'
              }`}
            >
              {isOverBudget ? '⚠️ Presupuesto Excedido' : '✓ Dentro del Presupuesto'}
            </p>
            <p
              className={`${classes.amount} font-bold ${
                isOverBudget ? 'text-red-600' : 'text-green-600'
              }`}
            >
              {isOverBudget ? '-' : '+'} {formatCurrency(Math.abs(available))}
            </p>
            <p className="text-xs text-gray-600 mt-1">
              {isOverBudget
                ? `Excedido por ${formatCurrency(Math.abs(available))}`
                : `Disponible para gastar`}
            </p>
          </div>
          <div
            className={`${classes.icon} rounded-full flex items-center justify-center flex-shrink-0 ${
              isOverBudget ? 'bg-red-100' : 'bg-green-100'
            }`}
          >
            {isOverBudget ? (
              <svg
                className={`${classes.iconSvg} text-red-600`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            ) : (
              <svg
                className={`${classes.iconSvg} text-green-600`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BudgetIndicator;
